import { useState } from "react";
import {
  Button,
  ContentSwitcher,
  DismissibleTag,
  FileUploaderButton,
  InlineLoading,
  Select,
  SelectItem,
  SelectItemGroup,
  Switch,
  Tag,
  TextArea,
  TextInput,
} from "@carbon/react";
import { Add } from "@carbon/icons-react";
import i18next from "../../../../i18n";
import { useFileInputUploader } from "../../../../hooks";
import { FileType, TO_BE_AGREED } from "../../../../utils";
import { DELIVERY_TYPE, OrderToSubmitRequest } from "../../../../types";
import {
  Currency,
  CURRENCY_OPTIONS,
} from "../../../../types/purchaseRequestV2.type";
import { SiteResponse } from "../../../../types/site.type";
import { objectiveOf } from "../../serviceDetail";
import ServiceLinesEditor from "./ServiceLinesEditor";

type ServiceDetailCardProps = {
  line: any;
  /** The quotation is this one service: it then carries the quotation's name and date. */
  single: boolean;
  onChange: (lineId: string, patch: Partial<OrderToSubmitRequest>) => void;
  sites?: SiteResponse[];
  handleSiteChange?: (lineId: string, siteId: string) => void;
  onCreateSite?: (lineId: string) => void;
};

const req = <span className="order-required"> *</span>;
const optional = (
  <span className="order-service-card__optional">
    {" "}
    {i18next.t("serviceDetail.optional")}
  </span>
);

/** The sites a delivery can go to, grouped by country as the v2 design lists them. */
const sitesByCountry = (sites: SiteResponse[] = []) =>
  sites.reduce<Record<string, SiteResponse[]>>((groups, site) => {
    const country = site.country?.name ?? "";
    (groups[country] = groups[country] ?? []).push(site);
    return groups;
  }, {});

/**
 * The service editor of the launch flow, v2 design (SCRUM-117, reworking SCRUM-112/113): name,
 * objective and scope, where and when, deliverables and an additional document, and the lines
 * suppliers price. On a joint quotation each service gets its own card and name, and the
 * quotation's name and date stay in the steps where they already are.
 */
const ServiceDetailCard = ({
  line,
  single,
  onChange,
  sites,
  handleSiteChange,
  onCreateSite,
}: ServiceDetailCardProps) => {
  const [fileInputKey, setFileInputKey] = useState(Date.now());
  const { mutate: uploadFile, isPending } = useFileInputUploader();
  const change = (patch: Partial<OrderToSubmitRequest>) => onChange(line.id, patch);
  const files = line.serviceFiles ?? [];
  const isPeriod = line.spot === DELIVERY_TYPE.PERIOD;

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    uploadFile(file, {
      onSuccess: (url: string) => {
        change({
          serviceFiles: [
            ...files,
            { name: file.name, fileURL: url, type: FileType.ATTACHMENT },
          ],
        });
        setFileInputKey(Date.now());
      },
    });
  };

  // Switching between once and a period starts the dates over, so a period never inherits a
  // single date as both of its ends.
  const setDateKind = (kind: string) =>
    change({ spot: kind, startSupplyDate: undefined, endSupplyDate: undefined });

  return (
    <section
      className="order-service-card"
      aria-label={`${i18next.t("serviceDetail.tag")} ${line.globalMaterialName ?? ""}`}
    >
      {!single && (
        <div className="order-service-card__head">
          <Tag type="warm-gray" size="sm">
            {i18next.t("serviceDetail.tag")}
          </Tag>
          <span className="order-service-card__name">{line.globalMaterialName}</span>
        </div>
      )}

      <div className="order-service-card__row order-service-card__row--name">
        {single ? (
          <TextInput
            id={`serviceName-${line.id}`}
            labelText={<>{i18next.t("serviceDetail.quotationName")}{req}</>}
            placeholder={i18next.t("serviceDetail.namePlaceholder")}
            value={line.name ?? ""}
            onChange={(e) => change({ name: e.target.value })}
          />
        ) : (
          <TextInput
            id={`serviceName-${line.id}`}
            labelText={<>{i18next.t("serviceDetail.serviceName")}{req}</>}
            placeholder={i18next.t("serviceDetail.namePlaceholder")}
            value={line.serviceTitle ?? ""}
            onChange={(e) => change({ serviceTitle: e.target.value })}
          />
        )}
        {single && (
          <TextInput
            id={`serviceRequestNumber-${line.id}`}
            labelText={<>{i18next.t("tenders.form.internalCode")}{optional}</>}
            value={line.internalCode ?? ""}
            onChange={(e) => change({ internalCode: e.target.value })}
          />
        )}
      </div>

      <TextArea
        id={`serviceObjective-${line.id}`}
        rows={3}
        maxLength={1000}
        labelText={<>{i18next.t("serviceDetail.objectiveAndScope")}{req}</>}
        placeholder={i18next.t("serviceDetail.objectiveAndScopePlaceholder")}
        value={objectiveOf(line)}
        // The one field also fills the purpose every older screen reads.
        onChange={(e) =>
          change({ serviceObjective: e.target.value, purpose: e.target.value })
        }
      />

      <div className="order-service-card__row">
        {handleSiteChange && (
          <div>
            <Select
              id={`site-${line.id}`}
              labelText={i18next.t("serviceDetail.deliveryPlace")}
              value={line.deliveryPlace?.id ?? ""}
              onChange={(e) => handleSiteChange(line.id, e.target.value)}
            >
              <SelectItem value="" text={TO_BE_AGREED} />
              {Object.entries(sitesByCountry(sites)).map(([country, group]) => (
                <SelectItemGroup key={country || "-"} label={country || "—"}>
                  {group.map((site) => (
                    <SelectItem key={site.id} value={site.id} text={site.name} />
                  ))}
                </SelectItemGroup>
              ))}
            </Select>
            {onCreateSite && (
              <Button
                kind="ghost"
                size="sm"
                renderIcon={Add}
                className="order-service-card__link"
                onClick={() => onCreateSite(line.id)}
              >
                {i18next.t("serviceDetail.addDeliveryPlace")}
              </Button>
            )}
          </div>
        )}

        {single && (
          <div className="order-service-card__field">
            <p className="cds--label">
              {i18next.t("serviceDetail.serviceDate")}
              {req}
            </p>
            <ContentSwitcher
              size="sm"
              selectedIndex={isPeriod ? 1 : 0}
              onChange={({ name }) => setDateKind(String(name))}
            >
              <Switch name={DELIVERY_TYPE.SPOT} text={i18next.t("serviceDetail.once")} />
              <Switch name={DELIVERY_TYPE.PERIOD} text={i18next.t("serviceDetail.period")} />
            </ContentSwitcher>
            {isPeriod ? (
              <div className="order-service-card__range">
                <TextInput
                  id={`serviceDateFrom-${line.id}`}
                  type="date"
                  labelText={i18next.t("tenders.form.from")}
                  value={line.startSupplyDate ?? ""}
                  onChange={(e) =>
                    change({ spot: DELIVERY_TYPE.PERIOD, startSupplyDate: e.target.value as any })
                  }
                />
                <span aria-hidden>→</span>
                <TextInput
                  id={`serviceDateTo-${line.id}`}
                  type="date"
                  labelText={i18next.t("tenders.form.to")}
                  min={line.startSupplyDate}
                  value={line.endSupplyDate ?? ""}
                  onChange={(e) =>
                    change({ spot: DELIVERY_TYPE.PERIOD, endSupplyDate: e.target.value as any })
                  }
                />
              </div>
            ) : (
              <TextInput
                id={`serviceDate-${line.id}`}
                type="date"
                labelText={i18next.t("serviceDetail.serviceDate")}
                hideLabel
                value={line.startSupplyDate ?? ""}
                // Once: the one date is both ends of the service.
                onChange={(e) =>
                  change({
                    spot: DELIVERY_TYPE.SPOT,
                    startSupplyDate: e.target.value as any,
                    endSupplyDate: e.target.value as any,
                  })
                }
              />
            )}
          </div>
        )}

        <Select
          id={`currency-${line.id}`}
          labelText={i18next.t("orderByRequest.itemCurrencyLabel")}
          value={line.coin ?? Currency.USD}
          onChange={(e) => change({ coin: e.target.value })}
        >
          {CURRENCY_OPTIONS.map((opt) => (
            <SelectItem key={opt.value} value={opt.value} text={opt.label} />
          ))}
        </Select>
      </div>

      <div className="order-service-card__row order-service-card__row--wide">
        <TextArea
          id={`serviceDeliverables-${line.id}`}
          rows={2}
          maxLength={2000}
          labelText={<>{i18next.t("serviceDetail.deliverables")}{optional}</>}
          placeholder={i18next.t("serviceDetail.deliverablesPlaceholder")}
          value={line.serviceDeliverables ?? ""}
          onChange={(e) => change({ serviceDeliverables: e.target.value })}
        />
        <div className="order-service-card__field">
          <p className="cds--label">
            {i18next.t("serviceDetail.additionalDocument")}
            {optional}
          </p>
          {isPending ? (
            <InlineLoading description={i18next.t("serviceDetail.addFile")} />
          ) : (
            <FileUploaderButton
              key={fileInputKey}
              buttonKind="tertiary"
              size="sm"
              labelText={i18next.t("serviceDetail.addFile")}
              disableLabelChanges
              onChange={handleFile}
            />
          )}
          {files.length > 0 && (
            <div className="order-service-card__chips">
              {files.map((file: any, index: number) => (
                <DismissibleTag
                  key={`${file.fileURL}-${index}`}
                  type="blue"
                  text={file.name}
                  title={i18next.t("serviceDetail.removeFile", { name: file.name })}
                  dismissTooltipLabel={i18next.t("serviceDetail.removeFile", {
                    name: file.name,
                  })}
                  onClose={() =>
                    change({
                      serviceFiles: files.filter((_: any, i: number) => i !== index),
                    })
                  }
                />
              ))}
            </div>
          )}
        </div>
      </div>

      <ServiceLinesEditor
        lineId={line.id}
        lines={line.serviceLines ?? []}
        onChange={(serviceLines) => change({ serviceLines })}
      />
    </section>
  );
};

export default ServiceDetailCard;
